import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'hooks/useSnackbar';
import { useRestApi } from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { SiteResponse } from './useSiteFiltering';
import { useCohortsActions } from './useCohortsActions';
import { useCohortsContext } from 'contexts/CohortsContext/CohortsContext';
import { handleApiError } from '../utils';

interface SitesApiResponse {
  items: SiteResponse[];
  next_token: string | null;
}

export type SearchType = 'captain_id' | 'learn_url' | 'client_name';

interface UseSiteSearchOptions {
  maxResults?: number;
}

export const useSiteSearch = ({ maxResults = 50 }: UseSiteSearchOptions = {}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedRegion } = useCohortsContext();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('captain_id');
  const [searchResults, setSearchResults] = useState<SiteResponse[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const { getCohortById } = useCohortsActions();

  const {
    data: singleSiteData,
    loading: loadingSingleSite,
    fetch: fetchSingleSite,
  } = useRestApi<SiteResponse>('', { manual: true });

  const {
    data: multiSiteData,
    loading: loadingMultiSites,
    fetch: fetchMultiSites,
  } = useRestApi<SitesApiResponse>(apiUrl('lct', `regions/${selectedRegion}/search`), {
    manual: true,
  });

  const isLoading = loadingSingleSite || loadingMultiSites;

  const shouldUseSingleEndpoint = useMemo(() => searchType === 'captain_id', [searchType]);

  const searchByCaptainId = useCallback(
    async (captainId: string) => {
      try {
        await fetchSingleSite({
          url: apiUrl('lct', `regions/${selectedRegion}/site/${captainId.trim()}`),
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          setSearchError(t('cohorts.search.errors.siteNotFound', { captainId }));
        } else {
          handleApiError(error, { operation: 'searchSites', enqueueSnackbar, t });
        }
        setSearchResults([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchSingleSite, selectedRegion],
  );

  const searchByFilter = useCallback(
    async (fieldName: string, value: string) => {
      try {
        const params: Record<string, string> = {
          results_count: maxResults.toString(),
        };

        if (fieldName === 'learn_url') {
          params.learn_url = value.trim();
        } else if (fieldName === 'client_name') {
          params.client_name = value.trim();
        }

        await fetchMultiSites({ params });
      } catch (error: any) {
        handleApiError(error, { operation: 'searchSites', enqueueSnackbar, t });
        setSearchResults([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetchMultiSites, maxResults],
  );

  const performSearch = useCallback(
    async (query?: string, type?: SearchType) => {
      const searchValue = query || searchQuery;
      const currentType = type || searchType;

      if (!searchValue.trim()) {
        setSearchError(t('cohorts.search.errors.searchTermRequired'));
        return;
      }

      setSearchError(null);
      setHasSearched(true);
      setSearchResults([]);

      if (currentType === 'captain_id') {
        await searchByCaptainId(searchValue);
      } else {
        await searchByFilter(currentType, searchValue);
      }
    },
    [searchQuery, searchType, searchByCaptainId, searchByFilter, t],
  );

  const processSingleSiteResult = useCallback(() => {
    if (singleSiteData) {
      setSearchResults([singleSiteData]);
      setSearchError(null);
    }
  }, [singleSiteData]);

  const processMultiSiteResult = useCallback(() => {
    if (multiSiteData?.items) {
      if (multiSiteData.items.length === 0) {
        const fieldType =
          searchType === 'learn_url'
            ? t('cohorts.search.types.learnUrl')
            : t('cohorts.search.types.clientName');

        setSearchError(
          t('cohorts.search.errors.noSitesFound', {
            fieldType,
            searchValue: searchQuery,
          }),
        );
        setSearchResults([]);
      } else {
        setSearchResults(multiSiteData.items);
        setSearchError(null);
      }
    }
  }, [multiSiteData, searchType, searchQuery, t]);

  useEffect(() => {
    if (shouldUseSingleEndpoint) {
      processSingleSiteResult();
    }
  }, [shouldUseSingleEndpoint, processSingleSiteResult]);

  useEffect(() => {
    if (!shouldUseSingleEndpoint) {
      processMultiSiteResult();
    }
  }, [shouldUseSingleEndpoint, processMultiSiteResult]);

  const getCohortInfo = useCallback(
    (site: SiteResponse) => {
      if (!site.cohort || site.cohort === 'NO_COHORT_ASSIGNED') {
        return {
          status: 'unassigned' as const,
          cohortId: null,
          cohortName: null,
          downtimeDate: null,
          displayText: t('cohorts.search.notAssignedToCohort'),
        };
      }

      const cohortDetails = getCohortById(site.cohort);

      return {
        status: 'assigned' as const,
        cohortId: site.cohort,
        cohortName: cohortDetails?.name || null,
        downtimeDate: cohortDetails?.downtime_date || site.cohort_downtime_date || null,
        displayText: cohortDetails?.name
          ? `${t('cohorts.search.assignedToCohort')}  ${cohortDetails.name}`
          : `${t('cohorts.search.assignedToCohort')}  ${site.cohort}`,
      };
    },
    [getCohortById, t],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);
  }, []);

  const changeSearchType = useCallback((newType: SearchType) => {
    setSearchType(newType);
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
    setHasSearched(false);
  }, []);

  return {
    searchQuery,
    searchType,
    searchResults,
    searchError,
    hasSearched,
    isLoading,

    resultsCount: searchResults.length,
    isSingleResult: searchResults.length === 1,
    isMultipleResults: searchResults.length > 1,

    setSearchQuery,
    setSearchType: changeSearchType,
    performSearch,
    clearSearch,
    getCohortInfo,
  };
};
